import type { ChemMoleculeV1Document } from './moleculeProjection.ts';
import type { ChemIsomerV1Document } from './isomerDocument.ts';
import type { PaperSize } from './geometry.ts';
import { buildIsomerDepictionPayload, type ParticipantDepiction } from '../chemistry/reactionParticipant.ts';

export interface IllustrationPosition {
  readonly x: number;
  readonly y: number;
}

// A picked-but-not-yet-saved illustration element, as accumulated by useIllustrationComposer.
// localId is a client-only identity (not part of the wire document) so placed elements can be
// dragged/labeled/removed before a real entity id + revision + position is known to be final.
export type IllustrationElementPick =
  | {
      readonly kind: 'isomer';
      readonly localId: string;
      readonly isomerId: string;
      readonly moleculeId: string;
      readonly isomerRevision: number;
      readonly position: IllustrationPosition;
      readonly label?: string;
    }
  | {
      readonly kind: 'molecule';
      readonly localId: string;
      readonly moleculeId: string;
      readonly moleculeRevision: number;
      readonly position: IllustrationPosition;
      readonly label?: string;
    }
  | {
      readonly kind: 'reaction';
      readonly localId: string;
      readonly reactionId: string;
      readonly reactionRevision: number;
      readonly position: IllustrationPosition;
      readonly label?: string;
    };

export interface IsomerIllustrationElement {
  readonly entityType: 'isomer';
  readonly id: string;
  readonly revision: number;
  readonly position: IllustrationPosition;
  readonly label?: string;
  readonly depiction: ParticipantDepiction;
  readonly annotations: readonly [];
}

export interface MoleculeIllustrationElement {
  readonly entityType: 'molecule';
  readonly id: string;
  readonly revision: number;
  readonly position: IllustrationPosition;
  readonly label?: string;
}

export interface ReactionIllustrationElement {
  readonly entityType: 'reaction';
  readonly id: string;
  readonly revision: number;
  readonly position: IllustrationPosition;
  readonly label?: string;
}

export type IllustrationElement = IsomerIllustrationElement | MoleculeIllustrationElement | ReactionIllustrationElement;

export interface IllustrationDrawingDocument {
  readonly schemaVersion: 'chem-drawing.v11';
  readonly kind: 'illustration';
  readonly elements: readonly IllustrationElement[];
  readonly title?: string;
}

/** #692 -- `isomerId` is the Isomer record's id, not the isomer document's internal `id`. */
export async function buildIsomerIllustrationElement(
  molecule: ChemMoleculeV1Document,
  isomer: ChemIsomerV1Document,
  isomerId: string,
  isomerRevision: number,
  position: IllustrationPosition,
  label: string | undefined,
  paperSize: PaperSize
): Promise<IsomerIllustrationElement> {
  return {
    entityType: 'isomer',
    id: isomerId,
    revision: isomerRevision,
    position,
    ...(label ? { label } : {}),
    depiction: await buildIsomerDepictionPayload(molecule, isomer, isomerId, isomerRevision, paperSize),
    annotations: []
  };
}

export function buildMoleculeIllustrationElement(
  moleculeId: string,
  moleculeRevision: number,
  position: IllustrationPosition,
  label: string | undefined
): MoleculeIllustrationElement {
  return {
    entityType: 'molecule',
    id: moleculeId,
    revision: moleculeRevision,
    position,
    ...(label ? { label } : {})
  };
}

export function buildReactionIllustrationElement(
  reactionId: string,
  reactionRevision: number,
  position: IllustrationPosition,
  label: string | undefined
): ReactionIllustrationElement {
  return {
    entityType: 'reaction',
    id: reactionId,
    revision: reactionRevision,
    position,
    ...(label ? { label } : {})
  };
}
